// @ts-nocheck
import { useState } from "react";
import { Plus, X, GripVertical, Upload } from "lucide-react";
import { FieldLabel, inputClass } from "./SectionWrapper";
import { Sponsor, EventSponsor } from "@/types/events";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";

interface Props {
  sponsors: Sponsor[];
  setSponsors: React.Dispatch<React.SetStateAction<Sponsor[]>>;
  eventSponsors: EventSponsor[];
  setEventSponsors: React.Dispatch<React.SetStateAction<EventSponsor[]>>;
  eventId?: string;
  markDirtyFromRelated?: () => void;
}

export function SponsorsSection({ sponsors, setSponsors, eventSponsors, setEventSponsors, eventId, markDirtyFromRelated }: Props) {
  const { createOrgId, ready: orgScopeReady } = useAdminOrgScope();
  const [showCreate, setShowCreate] = useState(false);
  const [newSponsor, setNewSponsor] = useState({ name: "", logo_url: "", website_url: "" });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [savingLink, setSavingLink] = useState(false);

  const normalizeWebsiteUrl = (url: string) => {
    const trimmed = url.trim();
    if (!trimmed) return null;
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  };

  const handleLogoUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) return;
    setUploadingLogo(true);
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `sponsors/sponsor-logo-${Date.now()}.${fileExt}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("org-logos")
        .upload(fileName, file, { cacheControl: "3600", upsert: true });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from("org-logos").getPublicUrl(uploadData.path);
      setNewSponsor((prev) => ({ ...prev, logo_url: urlData.publicUrl }));
    } catch (err: any) {
      console.error("Logo upload failed", err);
      toast.error(err?.message || "Logo upload failed");
    } finally {
      setUploadingLogo(false);
    }
  };

  const attachedIds = new Set(eventSponsors.map((es) => es.sponsor_id));
  const availableSponsors = sponsors.filter((s) => !attachedIds.has(s.id));
  const attachedSponsors = eventSponsors
    .sort((a, b) => a.display_order - b.display_order)
    .map((es) => ({ ...es, sponsor: sponsors.find((s) => s.id === es.sponsor_id) }))
    .filter((es) => es.sponsor);

  const attachSponsor = async (sponsorId: string) => {
    if (attachedIds.has(sponsorId)) return;
    const nextRow = {
      id: `temp-${Date.now()}`,
      event_id: eventId ?? "",
      sponsor_id: sponsorId,
      display_order: eventSponsors.length,
    };
    setEventSponsors((prev) => [...prev, nextRow]);
    markDirtyFromRelated?.();

    if (!eventId) return;
    setSavingLink(true);
    try {
      const { error } = await (supabase as any)
        .from("event_sponsors")
        .upsert(
          { event_id: eventId, sponsor_id: sponsorId, display_order: eventSponsors.length },
          { onConflict: "event_id,sponsor_id" }
        );
      if (error) throw error;
    } catch (err: any) {
      setEventSponsors((prev) => prev.filter((es) => es.sponsor_id !== sponsorId));
      console.error("Sponsor attach failed", err);
      toast.error(err?.message || "Sponsor attach failed");
    } finally {
      setSavingLink(false);
    }
  };

  const detachSponsor = async (sponsorId: string) => {
    const previous = eventSponsors;
    setEventSponsors((prev) => prev.filter((es) => es.sponsor_id !== sponsorId));
    markDirtyFromRelated?.();

    if (!eventId) return;
    setSavingLink(true);
    try {
      const { error } = await (supabase as any)
        .from("event_sponsors")
        .delete()
        .eq("event_id", eventId)
        .eq("sponsor_id", sponsorId);
      if (error) throw error;
    } catch (err: any) {
      setEventSponsors(previous);
      console.error("Sponsor remove failed", err);
      toast.error(err?.message || "Sponsor remove failed");
    } finally {
      setSavingLink(false);
    }
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    setEventSponsors((prev) => {
      const sorted = [...prev].sort((a, b) => a.display_order - b.display_order);
      [sorted[index - 1], sorted[index]] = [sorted[index], sorted[index - 1]];
      return sorted.map((s, i) => ({ ...s, display_order: i }));
    });
    markDirtyFromRelated?.();
  };

  const handleCreateSponsor = async () => {
    if (!newSponsor.name.trim()) return;
    if (!orgScopeReady) {
      toast.error("Organization is still loading. Please try again in a moment.");
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from("sponsors")
        .insert({
          name: newSponsor.name.trim(),
          logo_url: newSponsor.logo_url || null,
          website_url: normalizeWebsiteUrl(newSponsor.website_url),
          organization_id: createOrgId,
        })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        setSponsors((prev) => [...prev, data]);
        attachSponsor(data.id);
        setNewSponsor({ name: "", logo_url: "", website_url: "" });
        setShowCreate(false);
        toast.success("Sponsor saved");
      }
    } catch (err: any) {
      console.error("Sponsor save failed", err);
      toast.error(err?.message || "Sponsor save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Attached sponsors */}
      {attachedSponsors.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Attached Sponsors</h4>
          <div className="space-y-2">
            {attachedSponsors.map((es, i) => (
              <div key={es.sponsor_id} className="flex items-center gap-3 p-3 border border-border rounded-lg">
                <button type="button" onClick={() => moveUp(i)} className="cursor-grab text-muted-foreground hover:text-foreground">
                  <GripVertical className="w-4 h-4" />
                </button>
                {es.sponsor?.logo_url && (
                  <img src={es.sponsor.logo_url} alt="" className="w-8 h-8 object-contain rounded" />
                )}
                <span className="flex-1 text-sm font-medium">{es.sponsor?.name}</span>
                <button
                  type="button"
                  onClick={() => detachSponsor(es.sponsor_id)}
                  disabled={savingLink}
                  className="text-muted-foreground hover:text-red-500 transition-colors text-sm"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available sponsors */}
      {availableSponsors.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Available Sponsors</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {availableSponsors.map((s) => (
              <div key={s.id} className="flex items-center gap-3 p-3 border border-border rounded-lg">
                {s.logo_url && <img src={s.logo_url} alt="" className="w-8 h-8 object-contain rounded" />}
                <div className="flex-1">
                  <span className="text-sm font-medium">{s.name}</span>
                  {s.website_url && (
                    <span className="text-xs text-muted-foreground ml-2">{s.website_url}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => attachSponsor(s.id)}
                  disabled={savingLink}
                  className="px-3 py-1 bg-primary text-white rounded text-xs font-medium hover:opacity-90"
                >
                  Attach
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create sponsor */}
      {!showCreate ? (
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium"
        >
          <Plus className="w-4 h-4" />
          Create New Sponsor
        </button>
      ) : (
        <div className="p-4 border border-border rounded-lg space-y-3 bg-secondary/30">
          <h4 className="text-sm font-semibold">New Sponsor</h4>
          <div>
            <FieldLabel required>Name</FieldLabel>
            <input
              type="text"
              value={newSponsor.name}
              onChange={(e) => setNewSponsor({ ...newSponsor, name: e.target.value })}
              className={inputClass}
            />
          </div>
          <div>
            <FieldLabel>Logo</FieldLabel>
            {newSponsor.logo_url ? (
              <div className="flex items-center gap-3">
                <img src={newSponsor.logo_url} alt="" className="w-12 h-12 object-contain rounded border border-border" />
                <button
                  type="button"
                  onClick={() => setNewSponsor({ ...newSponsor, logo_url: "" })}
                  className="text-xs text-muted-foreground hover:text-red-500"
                >
                  Remove
                </button>
              </div>
            ) : (
              <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-border rounded-lg cursor-pointer text-sm text-muted-foreground hover:bg-secondary/50 w-fit">
                <Upload className="w-4 h-4" />
                {uploadingLogo ? "Uploading..." : "Upload Logo"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploadingLogo}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleLogoUpload(file);
                  }}
                />
              </label>
            )}
          </div>

          <div>
            <FieldLabel>Website</FieldLabel>
            <input
              type="text"
              inputMode="url"
              value={newSponsor.website_url}
              onChange={(e) => setNewSponsor({ ...newSponsor, website_url: e.target.value })}
              placeholder="https://..."
              className={inputClass}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleCreateSponsor}
              disabled={saving || !newSponsor.name.trim()}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Sponsor"}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCreate(false);
                setNewSponsor({ name: "", logo_url: "", website_url: "" });
              }}
              className="px-4 py-2 bg-secondary text-foreground rounded-lg text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}